/**
 * Functions for Calling J-Stage API Proxy
 *
 * @author Takuto Yanagida
 * @version 2024-07-02
 */

// Variable 'API_JSTAGE' is given by WordPress.
import t from './tlate/tlate.min.js';

document.addEventListener('DOMContentLoaded', async () => {
	const { apiUrl, journal } = window.API_JSTAGE;
	const dynamicList  = document.querySelector('.jstage-list[dynamic]');
	const defaultTitle = (s => {const i = s.indexOf('–'); return i !== -1 ? s.substring(i) : s;})(document.title);
	let dynamicHeading = document.querySelector('.jstage-heading');

	const lists  = document.querySelectorAll('.jstage-list');
	const items  = document.querySelectorAll('.jstage-item');
	const volSs  = document.querySelectorAll('.jstage-volume-select');
	const covers = document.querySelectorAll('.jstage-cover');

	window.addEventListener('hashchange', () => checkHash(location.hash));
	checkHash(location.hash);

	if (dynamicList) {
		let st = null;
		const mo1 = new MutationObserver(rs => {
			for (const r of rs) {
				if (r.attributeName.startsWith('data-')) {
					clearTimeout(st);
					st = setTimeout(() => processListElement(dynamicList), 100);
				}
			}
		});
		mo1.observe(dynamicList, { attributes: true });
	}
	for (const l of lists) {
		processListElement(l);
	}

	for (const a of items) processSingleElement(a);
	for (const s of volSs) processVolumeSelectElement(s);
	for (const c of covers) processCover(c);


	// -------------------------------------------------------------------------


	function setTargetBlank(parent) {
		const as = parent.querySelectorAll('a');
		for (const a of as) {
			a.setAttribute('target', '_blank');
			a.setAttribute('rel', 'noopener noreferrer');
		}
	}

	function updateListByUrl(url, year = null) {
		if (dynamicList) {
			const ms = url.match(/\/(\d+)\/([^\/]+)\/_contents$/);
			if (ms) {
				updateList(ms[1], ms[2], year);
				history.pushState(null, null, `#${ms[1]}-${ms[2]}`);
			}
		}
	}

	function checkHash(hash) {
		if (dynamicList) {
			const ms = hash.match(/#(\d+)-([^\/]+)/);
			if (ms) {
				updateList(ms[1], ms[2]);
			} else {
				updateList(null, null);
			}
		}
	}

	function updateList(vol, no, year = null) {
		if (vol && no) {
			dynamicList.removeAttribute('data-type');
			dynamicList.setAttribute('data-vol', vol);
			dynamicList.setAttribute('data-no', no);
			if (year) {
				dynamicList.setAttribute('data-year', year);
			} else {
				dynamicList.removeAttribute('data-year');
			}
		} else {
			dynamicList.setAttribute('data-type', 'current');
			dynamicList.removeAttribute('data-vol');
			dynamicList.removeAttribute('data-no');
			dynamicList.removeAttribute('data-year');
		}
	}

	function updateTitle(vol, no, year, list, updateDocTitle) {
		const title = `Vol. ${vol}, No. ${no} (${year})`;
		if (dynamicHeading) {
			dynamicHeading.innerText = title;
		} else {
			const h = document.createElement('h3');
			h.innerText = title;
			list.parentElement.insertBefore(h, list);
			dynamicHeading = h;
		}
		if (updateDocTitle) {
			document.title = `${title} ${defaultTitle}`;
			document.querySelector('meta[property="og:title"]').setAttribute('content', document.title);
		}
	}

	function getCurrentListUrl() {
		if (volSs.length) {
			const ms = location.hash.match(/#(\d+)-([^\/]+)/);
			if (ms) {
				const opt = volSs[0].querySelector(`option[data-hash="#${ms[1]}-${ms[2]}"]`);
				if (opt) {
					return opt.value;
				}
			}
		}
		return '';
	}

	function updateJstageLink() {
		const url = getCurrentListUrl();
		if (url) {
			const status = ensureListItem(dynamicList, 'jstage-status');
			if (status) {
				for (const c of Array.from(status.children)) {
					status.removeChild(c);
				}
				if (dynamicList.children.length <= 1) {
					const a = document.createElement('a');
					a.href = url;
					a.innerText = 'J-STAGE';
					status.append(a);
				}
			}
		}
	}


	// -------------------------------------------------------------------------


	function clearChildren(list, exception = null) {
		if (exception) {
			for (const c of Array.from(list.children)) {
				if (c.classList.contains(exception)) continue;
				list.removeChild(c);
			}
		} else {
			for (const c of Array.from(list.children)) {
				list.removeChild(c);
			}
		}
	}

	function ensureListItem(list, cls) {
		const es = list.getElementsByClassName(cls);
		if (es.length) {
			return es[0];
		}
		const e = document.createElement('li');
		e.classList.add(cls);
		list.appendChild(e);
		return e;
	}

	function HtmlToFragment(html) {
		const t = document.createElement('template');
		t.innerHTML = html;
		return t.content;
	}


	// -------------------------------------------------------------------------


	async function processListElement(list) {
		const type = list.dataset?.['type'] ?? null;
		if ('volume' === type) {
			processListElementVolume(list);
		} else {
			processListElementArticles(list, type);
		}
	}

	async function processListElementVolume(list) {
		const target = list.dataset?.['target'] ?? '';
		const empty  = list.dataset?.['empty'] ?? '';
		const status = ensureListItem(list, 'jstage-status');

		clearChildren(list, 'jstage-status');
		status.innerText = '';
		const vs = await getVolumes(journal);

		if (vs) {
			if (vs.length) {
				const df = HtmlToFragment(renderVolumeList(vs));
				list.insertBefore(df, status);
				status.innerText = '';
			} else {
				status.innerText = empty;
			}
		} else {
			status.innerText = 'Error';
		}
		if ('dynamic' === target) {
			const as = list.querySelectorAll('a');
			for (const a of as) {
				a.addEventListener('click', e => {
					e.preventDefault();
					updateListByUrl(a.href, a.dataset['year'] ?? null);
				});
			}
		} else if ('blank' === target) {
			setTargetBlank(list);
		}
	}

	async function processListElementArticles(list, type = null) {
		const vol = list.dataset?.['vol'] ?? null;
		const no  = list.dataset?.['no'] ?? null;
		if (!type && (!vol || !no)) {
			type = list.dataset?.['type'] ?? 'current';
		}
		const target = list.dataset?.['target'] ?? '';
		const empty  = list.dataset?.['empty'] ?? '';
		const status = ensureListItem(list, 'jstage-status');

		const view    = list.dataset?.['view'] ?? 'full';
		const orderby = list.dataset?.['orderby'] ?? null;
		const count   = list.dataset?.['count'] ?? null;

		let as = [];
		let doUpdateTitle = false;

		clearChildren(list, 'jstage-status');
		status.innerText = '';
		if ('advance' === type) {
			as = await getAdvancePublications(journal, orderby ? orderby : 'updated desc', count);
		} else if ('current' === type) {
			as = await getCurrentIssues(journal, orderby, count);
		} else {
			const year = list.dataset?.['year'] ?? '----';
			setTimeout(() => updateTitle(vol, no, year, list, true), 0);
			as = await getArticles(journal, vol, no, orderby, count);
			doUpdateTitle = true;
		}

		if (as) {
			if (as.length) {
				let df;
				if ('compact' === view) {
					df = HtmlToFragment(renderItemsCompact(as));
				} else {  // Default is 'full'.
					df = HtmlToFragment(renderItemsFull(as));
				}
				list.insertBefore(df, status);
				if (doUpdateTitle) updateTitle(as[0]['vol'], as[0]['no'], as[0]['year'], list, true);
				status.innerText = '';
			} else {
				if (empty.length) {
					status.innerText = empty;
				} else {
					updateJstageLink();
				}
			}
		} else {
			status.innerText = 'Error';
		}
		if ('blank' === target) {
			setTargetBlank(list);
		}
	}

	async function processSingleElement(div) {
		const url  = div.dataset?.['url'] ?? null;
		const target = div.dataset?.['target'] ?? '';

		if (!url) {
			div.innerText = 'URL is invalid.';
			return;
		}
		const it = await getArticle(journal, url);
		if (!it) {
			div.innerText = 'URL is invalid.';
			return;
		}
		div.dataset['view'] = 'single';
		const df = HtmlToFragment(renderItemCompact(it));
		div.appendChild(df);

		if ('blank' === target) {
			setTargetBlank(div);
		}
	}

	async function processVolumeSelectElement(sel) {
		const target = sel.dataset?.['target'] ?? '';

		const vs = await getVolumes(journal);
		clearChildren(sel);
		const opt = document.createElement('option');
		if (vs) {
			opt.innerText = 'Select a Volume';
			sel.appendChild(opt);
			sel.appendChild(HtmlToFragment(renderVolumeSelect(vs)));
			updateJstageLink();
		} else {
			opt.innerText = 'Error';
			sel.appendChild(opt);
		}

		if ('dynamic' === target) {
			sel.addEventListener('change', () => {
				const v  = sel.value;
				let year = null;
				if (sel.selectedIndex) {
					const opt = sel.options[sel.selectedIndex];
					year = opt.dataset['year'];
				}
				updateListByUrl(v, year);
			});
		} else if ('blank' === target) {
			sel.addEventListener('change', () => {
				const v = sel.value;
				sel.value = '';
				if (null === window.open(v, '_blank', 'noopener noreferrer')) {
					document.location.href = v;
				}
			});
		} else {
			sel.addEventListener('change', () => {
				const v = sel.value;
				sel.value = '';
				document.location.href = v;
			});
		}
	}

	async function processCover(div) {
		const url = div.dataset?.['url'] ?? null;

		if (!url) {
			div.innerText = 'URL is invalid.';
			return;
		}
		const it = await getCover(journal, url);
		if (!it) {
			div.innerText = 'URL is invalid.';
			return;
		}
		const img = document.createElement('img');
		img.src = it[0];
		div.appendChild(img);
	}


	// -------------------------------------------------------------------------


	async function getVolumes(journal) {
		const ps = { journal, action: 'volume' };
		return await fetchResponse(ps);
	}

	async function getCurrentIssues(journal, orderby = null, count = null) {
		const ps = { journal, action: 'current' };
		if (orderby) ps['orderby'] = orderby;
		if (count) ps['count'] = count;
		return await fetchResponse(ps);
	}

	async function getAdvancePublications(journal, orderby = null, count = null) {
		const ps = { journal, action: 'advance' };
		if (orderby) ps['orderby'] = orderby;
		if (count) ps['count'] = count;
		return await fetchResponse(ps);
	}

	async function getArticles(journal, vol, no, orderby = null, count = null) {
		const ps = { journal, action: 'articles', vol, no };
		if (orderby) ps['orderby'] = orderby;
		if (count) ps['count'] = count;
		return await fetchResponse(ps);
	}

	async function getArticle(journal, url) {
		const ps = { journal, action: 'article', url };
		return await fetchResponse(ps);
	}

	async function getCover(journal, url) {
		const ps = { journal, action: 'cover', url };
		return await fetchResponse(ps);
	}

	async function fetchResponse(ps) {
		try {
			const qs  = new URLSearchParams(ps).toString();
			const res = await fetch(`${apiUrl}?${qs}`);
			const d   = await res.text();
			return d ? JSON.parse(d) : null;
		} catch (e) {
			console.error(e);
			return null;
		}
	}


	// -------------------------------------------------------------------------


	function renderVolumeList(vols) {
		let ret = '';

		const ms = location.hash.match(/#(\d+)-([^\/]+)/);
		const v  = ms ? ms[1] : '';

		for (const vol of vols) {
			ret += t`
	<li>
		<details${(v === vol[0]['vol'] || (!v && '' === ret)) ? ' open' : ''}>
			<summary>Vol. ${vol[0]['vol']} (${vol[0]['year']})</summary>
			<ul>${() => vol.map(no => t`
				<li><a data-year="${vol[0]['year']}" href="${no['link_en']}">${isNaN(no['no']) ? '' : 'No. '}${no['no']}</a></li>`)}
			</ul>
		</details>
	</li>`;
		}
		return ret;
	}

	function renderVolumeSelect(vols) {
		let ret = '';

		for (const vol of vols) {
			ret += t`
	<optgroup label="Vol. ${vol[0]['vol']} (${vol[0]['year']})">
		${() => vol.map(no => t`
		<option data-hash="#${vol[0]['vol']}-${no['no']}" data-year="${vol[0]['year']}" value="${no['link_en']}">${isNaN(no['no']) ? '' : 'No. '}${no['no']}</option>`)}
	</optgroup>`;
		}
		return ret;
	}

	function renderItemsCompact(articles) {
		let ret = '';
		for (const a of articles) {
			ret += t`
	<li class="jstage-item">
		${() => renderItemCompact(a)}
	</li>`
		}
		return ret;
	}

	function renderItemsFull(articles) {
		let ret = '';
		for (const a of articles) {
			ret += t`
	<li class="jstage-item">
		${() => renderItemFull(a)}
	</li>`
		}
		return ret;
	}

	function renderItemCompact(a) {
		const authFmt = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });
		let vp = '';
		if ( 0 !== a['vol'] ) {
			const v = `Volume ${a['vol']} Issue ${a['no']}`;
			vp = t`${v}`;
		}

		const ret = t`
	<a href="${a['link_en']}">
		<div class="updated">${() => vp}</div>
		${() => a['image'] ? t`<div class="thumbnail"><img src="${a['image']}"></div>` : ''}
		<div class="title">${() => a['title_en']}</div>
		<div class="author">${authFmt.format(a['author_en'])}</div>
	</a>`
		return ret;
	}

	function renderItemFull(a) {
		let ret = '';
		const authFmt = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });
		const dateFmt = new Intl.DateTimeFormat('en-US', {
			weekday: 'narrow',
			year   : 'numeric',
			month  : 'short',
			day    : 'numeric',
		});

		let vp = '';
		if ( 0 !== a['vol'] ) {
			const v = `${a['year']} Volume ${a['vol']} Issue ${a['no']}`;
			const p = a['pp_from'] === a['pp_to'] ? `Page ${a['pp_from']}` : `Pages ${a['pp_from']}-${a['pp_to']}`;
			vp = t`<div>${v}; ${p};</div>`;
		}
		const d = dateFmt.format(new Date(a['updated'])).slice(3);

		ret += t`
	<a href="${a['link_en']}">
		<div class="title">${() => a['title_en']}</div>
		<div class="author">${authFmt.format(a['author_en'])}</div>
		<div class="info">
			${() => vp}
			<div><span>${d}</span>&emsp;<span class="doi">doi.org/${a['doi']}</span></div>
		</div>
		${() => a['image'] ? t`<div class="thumbnail"><img src="${a['image']}"></div>` : ''}
	</a>`
		return ret;
	}
});
